import React, { useEffect } from 'react';
import { connect as _connect, useSelector } from 'react-redux';
import { NotificationControl, deleteSATabSession, getBodyWidth, setEnforceOneTabItem, setOneTabSessionExpirationItem } from 'utils';
import { initApp, appError } from 'store/app/app.actions';
import { clearError, clearSuccess, clearSoftphoneError } from 'store/global/global.actions';
import RootState from 'store/state';

import { Popup, Slideout, Button } from '@missionlabs/smartagent-app-components';
import Menu from 'views/Menu';
import Login from 'views/Login';
import Error from 'views/Error';
import { PopupMessage, Notification } from 'components';

import './main-base.scss';
import Agents from 'views/Agents';
import { showAgents } from 'store/agents/agents.actions';
import { Redirect } from './Redirect';
import ErrorPopup from '../../components/ErrorPopup';
import LoggedInWrapper from './LoggedInWrapper';
import ErrorBoundary from 'components/ErrorBoundary';
import Main from 'views/Main';
import { AppFeatures, useCompanyHasFeature } from 'hooks/useHasFeature';

interface Props extends ReturnType<typeof mapStateToProps> { }

interface Actions {
    initApp: () => void;
    appError: typeof appError;
    clearError: () => void;
    clearSuccess: () => void;
    showAgents: () => void;
    clearSoftphoneError: () => void;
}

const MainBase: React.FC<Props & Actions>
    = ({ errorMessage,
        successMessage,
        showNotification,
        error,
        user,
        loading,
        authenticated,
        softphoneError,
        showIframe,
        agentsActive,
        logout,
        redirect,
        online, 
        saIsAlreadyOpen,
        ...actions
    }) => {

        const companyHasFeature = useCompanyHasFeature();
        const companyHasEnforceOneTabFeature = companyHasFeature(AppFeatures.ENFORCE_SA_ONE_TAB);

        useEffect(() => {
            NotificationControl.getPermission();
          // eslint-disable-next-line  
        }, [])

        useEffect(() => {
            const initialDelay = 1000
            const intervalDuration = 5000
            let intervalId: NodeJS.Timer | undefined;
            
            function startInterval() {
                setOneTabSessionExpirationItem()
                intervalId = setInterval(setOneTabSessionExpirationItem, intervalDuration)
            }
            if (saIsAlreadyOpen === false) {
                actions.initApp()
                window.addEventListener("beforeunload", function () {
                    deleteSATabSession()
                })
     
                if (intervalId) {
                    setTimeout(startInterval, initialDelay)
                } else {
                    startInterval()
                }
                return () => clearInterval(intervalId as NodeJS.Timer)
            }
        }, [saIsAlreadyOpen])
        useEffect(() => {
            if (saIsAlreadyOpen === false){
                setEnforceOneTabItem(companyHasEnforceOneTabFeature);
            }
        }, [companyHasEnforceOneTabFeature])


        const menuElem = document.querySelector('.menu');
        const bodyWidth = getBodyWidth();

        if (error) return <Error />;

        return (
            <ErrorBoundary appError={actions.appError}>
                
                <div id="app" className={showIframe ? 'show-iframe' : ''}>
                    {!!redirect && <Redirect to={redirect.to} state={redirect.state} />}

                    {(softphoneError === connect.SoftphoneErrorTypes.SIGNALLING_CONNECTION_FAILURE ||
                        softphoneError ===
                        connect.SoftphoneErrorTypes.SIGNALLING_HANDSHAKE_FAILURE) && (
                            <Popup center noShadow={false}>
                                <div className="top-modal">
                                    <h1>Call failed to connect</h1>
                                    <p>There was an issue with the network while connecting the call</p>
                                    <p>
                                        Refreshing the page can sometimes fix this issue. If the problem
                                        persists, please contact support.
                                    </p>
                                    <Button styling="primary" onClick={actions.clearSoftphoneError}>
                                        OK
                                    </Button>
                                </div>
                            </Popup>
                        )}

                    <Slideout
                        slideDuration={300}
                        isMounted={agentsActive}
                        onOverlayClick={actions.showAgents}
                        width={bodyWidth < 520 ? bodyWidth - 70 : 450}
                        offset={(menuElem?.clientWidth) || 60}
                        from="left"
                        overlay>
                        <Agents />
                    </Slideout>

                    {loading || logout || saIsAlreadyOpen  ? ( //If we have a specific logout then show auth screen otherwise depends if instance is selected or not
                        <Login />
                    ) : authenticated && user ? (
                        <LoggedInWrapper>
                            <div className="content">
                                <Menu />
                                <Main />
                            </div>
                        </LoggedInWrapper>
                    ) : (
                        <Login />
                    )}

                    {!!errorMessage && (
                        <PopupMessage clearMessage={actions.clearError} text={errorMessage} />
                    )}
                    {!!successMessage && (
                        <PopupMessage
                            clearMessage={actions.clearSuccess}
                            text={successMessage}
                            type="success"
                        />
                    )}
                    {!!showNotification && (
                        <Notification
                            type={showNotification.type}
                            header={showNotification.header}
                            text={showNotification.text}
                        />
                    )}
                    {!online && <ErrorPopup text='You&apos;re not currently online. Please check your internet connection.' online={online} />}
                </div>
            </ErrorBoundary>
        );

    }

function mapStateToProps(state: RootState) {
    return {
        logout: state.auth.logout || false,
        error: state.app.error,
        softphoneError: state.global.softphoneError,
        showIframe: state.global.showIframe,
        authenticated: state.auth.authenticated,
        loading: state.app.loading,
        user: !!state.user,
        errorMessage: state.global.error,
        successMessage: state.global.success,
        showNotification: state.global.showNotification,
        agentsActive: state.agents.agentsActive,
        redirect: state.global.redirect,
        online: state.global.online,
        saIsAlreadyOpen: state.global.saIsAlreadyOpen,
    };
}

export default _connect(mapStateToProps, {
    initApp,
    appError,
    clearError,
    clearSuccess,
    showAgents,
    clearSoftphoneError,
})(MainBase);
